import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Trash2 } from "lucide-react";

const MIN_REASON = 5;

/**
 * Confirmação de exclusão com resumo de impacto.
 * Busca o impacto no backend (dependências reais) antes de permitir a execução.
 * Com `requireReason`, exige justificativa (gravada na lixeira e na auditoria).
 */
export default function ConfirmDeleteDialog({
  open, onOpenChange, entity = "registro", entityLabel = "", impactUrl, deleteUrl,
  onDeleted, cascadeLabel = "", requireReason = false, testId = "confirm-delete-dialog",
}) {
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setImpact(null);
    setCascade(false);
    setReason("");
    if (impactUrl) {
      api.get(impactUrl)
        .then(({ data }) => setImpact(data))
        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    }
  }, [open, impactUrl]);

  const confirm = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (cascadeLabel && cascade) params.set("delete_occurrences", "true");
      if (requireReason) params.set("reason", reason.trim());
      const query = params.toString();
      const url = query ? `${deleteUrl}${deleteUrl.includes("?") ? "&" : "?"}${query}` : deleteUrl;
      await api.delete(url);
      toast.success(`${entity.charAt(0).toUpperCase() + entity.slice(1)} excluído(a) com sucesso`);
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deps = (impact?.dependencies || []).filter((d) => d.count > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" /> Excluir {entity}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-sm font-semibold" data-testid="delete-entity-label">{entityLabel || impact?.label || "—"}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Esta informação pode ser importante para o histórico operacional. Ao confirmar, o registro sai das listas,
              dos indicadores e dos relatórios. O registro de auditoria é mantido permanentemente.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Impacto da operação</p>
            {!impact && <p className="text-sm text-muted-foreground">Calculando impacto...</p>}
            {impact && deps.length === 0 && <p className="text-sm text-muted-foreground">Nenhum registro dependente.</p>}
            {impact && deps.length > 0 && (
              <ul className="space-y-1.5" data-testid="delete-impact-list">
                {deps.map((d) => (
                  <li key={d.label} className="flex items-center justify-between text-sm rounded-md border border-border px-3 py-1.5">
                    <span>{d.label}</span>
                    <span className="text-xs text-muted-foreground">{d.count} · {d.action}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {requireReason && (
            <div className="space-y-1.5">
              <label htmlFor="delete-reason" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Justificativa (obrigatória)
              </label>
              <Textarea
                id="delete-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex.: registro duplicado, lançado no cliente errado..."
                rows={3}
                data-testid="delete-reason-input"
              />
              <p className="text-xs text-muted-foreground">
                O registro vai para a lixeira e pode ser restaurado por quem tiver permissão.
              </p>
            </div>
          )}

          {cascadeLabel && (
            <label className="flex items-start gap-2 text-sm rounded-md border border-border px-3 py-2.5 cursor-pointer">
              <Checkbox checked={cascade} onCheckedChange={(v) => setCascade(!!v)} data-testid="delete-cascade-check" />
              <span>{cascadeLabel}</span>
            </label>
          )}

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="delete-cancel-btn">Cancelar</Button>
            <Button variant="destructive" className="flex-1" onClick={confirm}
              disabled={busy || (requireReason && reason.trim().length < MIN_REASON)} data-testid="delete-confirm-btn">
              <Trash2 size={14} className="mr-2" /> {busy ? "Excluindo..." : "Excluir"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
